import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from "react";
import { SpinnerIcon } from "@/shared/components/icons";
import { cn } from "@/shared/lib/cn";
import { messages } from "@/shared/i18n";

type ButtonVariant = "primary" | "secondary" | "danger";
type ButtonSize = "sm" | "md" | "lg";

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  icon?: ReactNode;
  isLoading?: boolean;
  loadingText?: string;
  fullWidth?: boolean;
}

function LoadingSpinner({ className }: { className?: string }) {
  return <SpinnerIcon aria-hidden="true" className={cn("animate-spin", className)} />;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant = "primary",
      size = "md",
      icon,
      isLoading = false,
      loadingText,
      fullWidth = false,
      disabled,
      children,
      type = "button",
      ...props
    },
    ref
  ) => {
    const isDisabled = disabled || isLoading;

    return (
      <button
        ref={ref}
        className={cn(
          // Base styles
          "inline-flex cursor-pointer items-center justify-center gap-2 rounded-lg font-semibold",
          "transition-all duration-300",
          "focus-visible:ring-brand-primary focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none",
          "disabled:cursor-not-allowed disabled:opacity-50",
          // Size styles
          size === "sm" && "px-3 py-1.5 text-sm",
          size === "md" && "px-4 py-3 text-base",
          size === "lg" && "px-6 py-4 text-lg",
          // Variant styles
          variant === "primary" && [
            "bg-gradient-brand shadow-button text-white",
            "hover:bg-gradient-brand-hover hover:shadow-lg",
            "disabled:hover:shadow-button",
          ],
          variant === "secondary" && [
            "border-brand-primary text-brand-primary border-2 bg-white",
            "hover:bg-brand-primary hover:text-white",
            "disabled:hover:text-brand-primary disabled:hover:bg-white",
          ],
          variant === "danger" && [
            "border-2 border-red-300 bg-white text-red-600",
            "hover:border-red-400 hover:bg-red-50",
            "disabled:hover:border-red-300 disabled:hover:bg-white",
          ],
          // Full width
          fullWidth && "w-full",
          className
        )}
        disabled={isDisabled}
        type={type}
        {...props}
      >
        {isLoading ? (
          <>
            <LoadingSpinner className="h-5 w-5" />
            <span>{loadingText ?? messages.common.loading}</span>
          </>
        ) : (
          <>
            {icon && <span className="h-5 w-5 [&>svg]:h-full [&>svg]:w-full">{icon}</span>}
            {children}
          </>
        )}
      </button>
    );
  }
);

Button.displayName = "Button";
